<template>
  <InterfaceModalBase
    :showModal="showModal"
    @update:showModal="$emit('update:showModal', $event)"
    size-tailwind-class="sm:max-w-3xl"
    :showButtons="true"
  >
    <template #heading> {{ nodeType }} </template>
    <template #title>
      <span
        v-if="props.filterByDonorCode != ''"
        class="block text-base text-left text-gray-600"
        >{{ props.filterByDonorName }} to: </span
      >{{ entityName }}
      </template>

    <template #content>
      <div class="divider mt-4 mb-6 h-[1px] w-full bg-Neutral-400" />
      <div v-if="props.yearsFilter.length > 0" class="mb-5 text-left -mt-2">
        <h3 class="!text-neutral-500 mini-title">
          {{ props.yearsFilter[0] }} - {{ props.yearsFilter[1] }}
        </h3>
      </div>
      <!--- display info from a country -->
      <div class="text-left">
        <div class="mb-5">
          <h3 class="mini-title">SPENDING CATEGORIES</h3>
          <aggregatedStackedHorizontal
            :series="allCategoriesReadyForStacked"
            :showLegend="false"
            :showLabels="false"
            :barHeight="60"
            :colorScaleExternal="categoryColorScale"
            rectClass="stroke-white stroke-2 hover:brightness-110"
            :roundedRadius="4"
            :axisWidth="0"
          ></aggregatedStackedHorizontal>
        </div>
        <div class="mb-5">
          <h3 class="mini-title">Top 5 categories</h3>
          <div class="flex gap-1 mb-8 flex-wrap">
            <InterfaceLabelBadge
              v-for="(
                cat, index
              ) in allCategoriesReadyForStacked[0].children.slice(0, 5)"
              :key="index"
              :category="cat.id"
              parentId="geomodal"

            />   
          </div>
        </div>
        <div class="mb-5" v-if="nodeType === nodeTypes.donor">
          <h3 class="mini-title">Top recipient countries</h3>
          <div>
            <span v-for="country in recipientsWithCode" class="mr-2">
              <nuxt-link
                v-if="country.recipient_code"
                :to="'/recipients/' + country.recipient_code"
                class="link"
                disabled
              >
                {{ country.label }} ({{
                  $formater.salaryFormater(country.Total)
                }})
              </nuxt-link>
              <template v-else>
                {{ country.label }} ({{
                  $formater.salaryFormater(country.Total)
                }})
              </template>
            </span>
          </div>
        </div>
      </div>
    </template>
    <template #buttons>
      <nuxt-link :to="linkToUrl" class="btn primary">
        Explore {{ entityName }}
      </nuxt-link>
    </template>
  </InterfaceModalBase>
</template>

<script setup>
import { nodeTypes } from "@/assets/configStrings.js";
import { httpConfig } from "@/assets/baseTypes.js";
import { queries } from "@/assets/queries.js";
import { categoryColorScale } from "@/assets/baseTypes.js";
import allRecipients from "@/assets/recipients.json";
import { scaleLinear, sum, descending } from "d3";
const props = defineProps({
  showModal: {
    type: Boolean,
    default: true,
  },
  nodeType: {
    type: String,
    default: nodeTypes.Donor,
  },
  closeAtClickOutside: {
    type: Boolean,
    default: true,
  },
  entityName: {
    type: String,
    default: "entity",
  },
  entityId: {
    type: String,
    default: "GER",
  },
  filterByDonorCode: {
    type: String,
    default: "",
  },
  filterByDonorName: {
    type: String,
    default: "",
  },
  recipients: {
    type: Array,
    default: () => [],
  },
  yearsFilter: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(["update:showModal"]);

const config = useRuntimeConfig();
watch(
  () => props.entityName,
  (newValue, oldValue) => {
    pullCategoriesForEntity();
  }
);

const linkToUrl = computed(() => {
  if (props.nodeType === nodeTypes.donor) {
    return "/bydonors/" + props.entityId;
  }
  if (props.nodeType === nodeTypes.recipient) {
    return "/recipients/" + props.entityId;
  }
});

const isLoading = ref(false);

const categories = ref([]);

// categories:[
//       {
//         name: "category1",
//         value: 1,
//       }, ...
//     ],

function pullCategoriesForEntity() {
  isLoading.value = true;
  /** pull data in case this is a region **/
  var configQuery = null;
  const years = props.yearsFilter.length > 0 ? props.yearsFilter : undefined;
  if (props.nodeType === nodeTypes.recipient && props.filterByDonorCode == "") {
    configQuery = httpConfig(
      JSON.stringify(
        queries.getAllCategoriesOneRecipient(props.entityId + "", years)
      )
    );
  } else if (
    props.nodeType === nodeTypes.recipient &&
    props.filterByDonorCode != ""
  ) {
    configQuery = httpConfig(
      JSON.stringify(
        queries.getAllCategoriesOneRecipientOneDonor(
          props.entityId + "",
          props.filterByDonorCode,
          years
        )
      )
    );
  } else if (props.nodeType === nodeTypes.donor) {
    configQuery = httpConfig(
      JSON.stringify(
        queries.getAllCategoriesOneDonor(props.entityId + "", years)
      )
    );
  }

  $fetch(config.public.apiURL, configQuery)
    .then((res) => {
      categories.value = res.documents.map((d) => {
        return {
          id: d._id,
          value: d.TOTAL,
        };
      });
    })
    .catch((err) => {
      console.log(err);
        // useReloadTokenOnError().refreshReload()
    })
    .finally(() => {
      isLoading.value = false;
    });
}

const categoriesWithRelative = computed(() => {
  const total = sum(categories.value, (d) => d.value);
  return categories.value
    .map((d) => {
      return {
        ...d,
        valueRelative: d.value / total,
      };
    })
    .sort((a, b) => b.value - a.value);
});

const allCategoriesReadyForStacked = computed(() => {
  return [
    {
      id: "",
      value: sum(categories.value, (d) => d.value),
      children: categoriesWithRelative.value,
    },
  ];
});

const recipientsWithCode = computed(() => {
  return props.recipients.map((r) => {
    return {
      ...r,
      recipient_code: allRecipients.find((rec) => rec.iso === r._id)
        ?.recipient_code,
    };
  });
});
</script>

<style lang="scss" scoped></style>
